import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trophy, Loader2, RefreshCw, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Badge, useToast } from '@hiring-intelligence/ui';
import { candidateAiApi, sourcingApi } from '@/services/api';

const TIER_BADGE: Record<string, string> = {
	top: 'bg-emerald-100 text-emerald-700',
	strong: 'bg-blue-100 text-blue-700',
	possible: 'bg-amber-100 text-amber-700',
	stretch: 'bg-slate-100 text-slate-600'
};

const dateFmt = (d: string) =>
	new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * "Candidate Ranking" — section at the top of the Candidates (saved candidates) tab.
 * Manual trigger: ranks the recommended candidates against the role context and writes
 * a pipeline summary. Versioned; re-ranks are incremental — the previous version's
 * assessments are reused so only new candidates cost full tokens.
 */
export function CandidateRankingCard({ hiringRequestId }: { hiringRequestId: string }) {
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [expanded, setExpanded] = useState<Record<string, boolean>>({});

	const { data, isLoading } = useQuery({
		queryKey: ['candidate-rankings', hiringRequestId],
		queryFn: () => candidateAiApi.listRankings(hiringRequestId),
		refetchOnWindowFocus: false,
		refetchInterval: (query) => {
			const versions = query.state.data?.data || [];
			return versions.some((v: any) => v.status === 'processing') ? 5000 : false;
		}
	});

	// Shares the saved-candidates cache with CandidateProcessList (same query key).
	const { data: candidatesData } = useQuery({
		queryKey: ['sourced-candidates', hiringRequestId],
		queryFn: () => sourcingApi.getCandidates(hiringRequestId),
		refetchOnWindowFocus: false
	});

	const versions: any[] = data?.data || [];
	const selected = versions.find((v) => v.id === selectedId) ?? versions[0] ?? null;
	const isProcessing = versions.some((v) => v.status === 'processing');
	const latestCompleted = versions.find((v) => v.status === 'completed') ?? null;

	// Only recommended candidates are ranked (mirrors the worker's query).
	const rankableCandidates = ((candidatesData?.data as any[]) || []).filter(
		(c) => c.recommended && !['rejected', 'withdrawn'].includes(c.status)
	);
	const rankedIds = new Set<string>((latestCompleted?.content?.rankings || []).map((r: any) => r.candidateId));
	const newCount = rankableCandidates.filter((c) => !rankedIds.has(c.id)).length;

	const generateMutation = useMutation({
		mutationFn: () => candidateAiApi.generateRanking(hiringRequestId),
		onSuccess: () => {
			setSelectedId(null);
			queryClient.invalidateQueries({ queryKey: ['candidate-rankings', hiringRequestId] });
			toast({ title: 'Ranking started', description: 'Evaluating the recommended candidates.' });
		},
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	const content = selected?.status === 'completed' ? selected.content : null;
	const buttonLabel = !latestCompleted
		? 'Rank Candidates'
		: newCount > 0
			? `Update Ranking (${newCount} new)`
			: 'Re-rank';

	return (
		<Card>
			<CardHeader>
				<div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
					<div>
						<CardTitle className="flex items-center gap-2">
							<Trophy className="h-5 w-5 text-primary" />
							Candidate Ranking
							{selected?.isIncremental && (
								<Badge variant="outline" className="text-[10px]">Incremental · base v{selected.baseVersion}</Badge>
							)}
						</CardTitle>
						<CardDescription className="mt-1">
							Ranks the recommended candidates against the role criteria with a pipeline summary.
							{latestCompleted && newCount > 0 && (
								<span className="text-primary font-medium"> {newCount} candidate{newCount === 1 ? '' : 's'} recommended since the last ranking.</span>
							)}
						</CardDescription>
					</div>
					<div className="flex items-center gap-2 shrink-0">
						{versions.length > 0 && (
							<select
								className="h-9 rounded-md border border-input bg-transparent px-2 text-xs"
								value={selected?.id || ''}
								onChange={(e) => setSelectedId(e.target.value)}
							>
								{versions.map((v) => (
									<option key={v.id} value={v.id}>
										v{v.version} · {dateFmt(v.createdAt)}{v.requestedBy?.name ? ` · ${v.requestedBy.name}` : ''}{v.status !== 'completed' ? ` · ${v.status}` : ''}
									</option>
								))}
							</select>
						)}
						<Button
							size="sm"
							onClick={() => generateMutation.mutate()}
							disabled={isProcessing || generateMutation.isPending || rankableCandidates.length === 0}
						>
							{isProcessing || generateMutation.isPending
								? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
								: <RefreshCw className="mr-1.5 h-4 w-4" />}
							{buttonLabel}
						</Button>
					</div>
				</div>
			</CardHeader>
			<CardContent>
				{isLoading ? (
					<div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
						<Loader2 className="h-4 w-4 animate-spin" /> Loading…
					</div>
				) : versions.length === 0 ? (
					<p className="text-sm text-muted-foreground py-2">
						{rankableCandidates.length === 0
							? 'Recommend candidates below first, then rank them here.'
							: `No ranking yet for the ${rankableCandidates.length} recommended candidate${rankableCandidates.length === 1 ? '' : 's'}. Click Rank Candidates to start.`}
					</p>
				) : selected?.status === 'processing' ? (
					<div className="flex items-center gap-3 py-6 justify-center text-sm text-muted-foreground">
						<Loader2 className="h-5 w-5 animate-spin text-primary" />
						Evaluating the recommended candidates…
					</div>
				) : selected?.status === 'failed' ? (
					<div className="flex items-start gap-2 py-3 text-sm text-destructive">
						<AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
						<span>Ranking failed: {selected.error || 'Unknown error'}. Try again.</span>
					</div>
				) : content ? (
					<div className="space-y-4">
						{content.summary && <p className="text-sm border-l-2 border-primary/30 pl-3">{content.summary}</p>}
						<div className="space-y-2">
							{(content.rankings || []).map((r: any) => (
								<div key={r.candidateId} className="rounded-lg border p-3">
									<div
										className="flex items-center gap-3 cursor-pointer"
										onClick={() => setExpanded((p) => ({ ...p, [r.candidateId]: !p[r.candidateId] }))}
									>
										<span className="text-lg font-bold text-muted-foreground w-8 text-center shrink-0">#{r.rank}</span>
										<div className="min-w-0 flex-1">
											<p className="font-medium text-sm truncate">{r.candidateName}</p>
											<p className="text-xs text-muted-foreground truncate">{r.highlight}</p>
										</div>
										<span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${TIER_BADGE[r.tier] || TIER_BADGE.stretch}`}>
											{r.tier}
										</span>
										<span className="text-sm font-semibold shrink-0 w-10 text-right">{r.score}</span>
										{expanded[r.candidateId]
											? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
											: <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
									</div>
									{expanded[r.candidateId] && (
										<div className="mt-2 pl-11 text-xs text-muted-foreground space-y-1">
											<p>{r.reasoning}</p>
											{r.carriedOver && <p className="text-[10px] italic">Assessment carried over from v{selected.baseVersion}.</p>}
										</div>
									)}
								</div>
							))}
						</div>
						<p className="text-[10px] text-muted-foreground text-right">v{selected.version} · {dateFmt(selected.createdAt)}</p>
					</div>
				) : null}
			</CardContent>
		</Card>
	);
}
